import {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useCallback,
  useRef,
  useState,
} from "react";
import {
  fetchUser,
  fetchSelfUser,
  createUser,
  claimBetaReward as claimBetaRewardService,
  resendUserVerificationCode,
  verifyUserCode,
} from "../shared/services";
import { SubscriptionType } from "../shared/constants";
import type { User, FBUser } from "../shared/types";
import { normalizeUserIdentifier } from "../shared/identifiers";
import { DateTime } from "luxon";
import toast from "react-hot-toast";
import { useNavigate, useLocation } from "react-router";
import { auth } from "../shared/firebase";
import { setSessionExpiredHandler } from "../shared/axios";
import {
  createUserWithEmailAndPassword,
  deleteUser as deleteFirebaseUser,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signInWithPopup,
  signOut,
  type AuthProvider as FirebaseAuthProvider,
  type User as FirebaseUser,
  type UserCredential,
} from "firebase/auth";
import { loadSingleTranslation } from "../lib/load-i18n";
import { getRedactedOrNodeOrFallback } from "../lib/i18n-helpers";
import { I18nContext } from "./i18n";
import AuthLoadingState from "../components/auth/AuthLoadingState";
import { POSTHOG_EVENTS } from "../shared/posthogEvents";
import { analytics } from "../shared/utils";

const ADMIN_REDIRECT_STORAGE_KEY = "admin_redirect_path";
type AuthUserProfile = User | FBUser;

const deriveUserTypeFromProfile = (profile?: {
  type?: string;
  userType?: string | null;
  role?: string;
}) => {
  const normalizedRole =
    typeof profile?.role === "string" ? profile.role.toLowerCase() : "";
  if (normalizedRole === "admin") return "admin";

  const rawType: unknown =
    profile?.userType || profile?.type || profile?.role;
  const normalizedType =
    typeof rawType === "string"
      ? rawType.toLowerCase()
      : String(rawType ?? "").toLowerCase();

  if (normalizedType === "admin") return "admin";
  if (normalizedType === "partner") return "partner";
  if (normalizedType === "organization") return "organization";
  if (normalizedType === "api") return "api";
  return "app-user";
};

const isOrganizationUserType = (userType?: string | null) =>
  userType === "organization" || userType === "api";

const getLoginMethodFromProviderIds = (providerIds: string[]) => {
  if (providerIds.includes("google.com")) return "google";
  if (providerIds.includes("apple.com")) return "apple";
  if (providerIds.includes("password")) return "email_password";
  return providerIds[0] || "unknown";
};

type AuthContextType = {
  user: any | null;
  loadingAuth: boolean;
  /**
   * An email/password or provider sign-in started from the login form is in
   * flight. Separate from loadingAuth on purpose: loadingAuth swaps the
   * whole tree for AuthLoadingState, which would yank the form away
   * mid-submit. The form shows its own in-button progress for this instead.
   */
  signingIn: boolean;
  isImpersonatingFromAdmin: boolean;
  loginWithUID: (uid: string) => void;
  loginAsUserFromAdmin: (uid: string) => void;
  stopImpersonation: () => void;
  login: (user: string, pwd: string) => void;
  loginWithProvider: (
    provider: FirebaseAuthProvider,
    loginMethod: string,
  ) => Promise<void>;
  /** Rejects (after a toast) when sign-in fails. */
  loginWithLineToken: (customToken: string) => Promise<void>;
  register: (email: string, password: string) => void;
  setUser: (user: any) => void;
  logout: () => void;
  /**
   * After the backend has deleted the account: deletes the Firebase Auth
   * user, then signs out like `logout` and lands on /login. The session is
   * torn down before the Firebase delete so the auth listener can't
   * rehydrate the deleted account from the stored hub session.
   */
  signOutDeletedAccount: () => Promise<{ firebaseDeleteFailed: boolean }>;
  refreshUser: () => Promise<void>;
  resendVerificationCode: () => Promise<void>;
  verifyAccountCode: (code: string) => Promise<void>;
  /** Where the next sign-in lands (defaults to the current page). */
  rememberRedirectPath: (path?: string | null) => void;
};

// Context to store the user
export const AuthContext = createContext<AuthContextType>({
  user: null,
  loadingAuth: false,
  signingIn: false,
  isImpersonatingFromAdmin: false,
  loginWithUID: (uid: string) => {},
  loginAsUserFromAdmin: (uid: string) => {},
  stopImpersonation: () => {},
  login: (user, pwd) => Promise.resolve(),
  loginWithProvider: () => Promise.resolve(),
  loginWithLineToken: () => Promise.resolve(),
  register: (email, password) => Promise.resolve(),
  setUser: (user: any) => {},
  logout: () => {},
  signOutDeletedAccount: () => Promise.resolve({ firebaseDeleteFailed: false }),
  refreshUser: () => Promise.resolve(),
  resendVerificationCode: () => Promise.resolve(),
  verifyAccountCode: (code: string) => Promise.resolve(),
  rememberRedirectPath: () => {},
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { lang } = useContext(I18nContext);
  const [user, setUser] = useState<any | null>(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  const [authI18n, setAuthI18n] = useState<Record<string, any>>({});
  const [isImpersonatingFromAdmin, setIsImpersonatingFromAdmin] =
    useState(false);
  const isLoggingOutRef = useRef(false);
  const fetchOrCreateSelfPromisesRef = useRef<
    Record<string, Promise<AuthUserProfile | null>>
  >({});
  const [redirectPath, setRedirectPath] = useState<string | null>(() => {
    try {
      return localStorage.getItem(ADMIN_REDIRECT_STORAGE_KEY);
    } catch (error) {
      console.warn("Unable to access redirect storage", error);
      return null;
    }
  });
  const navigate = useNavigate();

  const getAuthI18nValue = useCallback(
    (node: string, fallback: string) =>
      getRedactedOrNodeOrFallback(authI18n, node, fallback),
    [authI18n],
  );

  useEffect(() => {
    let active = true;

    const loadAuthTranslations = async () => {
      const values = await loadSingleTranslation(lang, "components");
      if (active) {
        setAuthI18n(values.components || {});
      }
    };

    loadAuthTranslations();

    return () => {
      active = false;
    };
  }, [lang]);

  const persistHubSession = useCallback(
    (
      userId: string,
      userType: string,
      impersonatingFromAdmin: boolean = false,
    ) => {
    localStorage.setItem(
      "hub",
      JSON.stringify({
        timestamp: DateTime.now(),
        uuid: userId,
        userType,
        isImpersonatingFromAdmin: impersonatingFromAdmin,
      }),
    );
    },
    [],
  );

  const rememberRedirectPath = useCallback((path?: string | null) => {
    const targetPath =
      path ||
      `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (!targetPath || targetPath === "/login") {
      return;
    }
    setRedirectPath(targetPath);
    localStorage.setItem(ADMIN_REDIRECT_STORAGE_KEY, targetPath);
  }, []);

  const clearRedirectPath = useCallback(() => {
    setRedirectPath(null);
    localStorage.removeItem(ADMIN_REDIRECT_STORAGE_KEY);
  }, []);

  const handlePostLoginNavigation = useCallback(
    (userType?: string | null) => {
      const pendingRedirect =
        redirectPath || localStorage.getItem(ADMIN_REDIRECT_STORAGE_KEY);

      if (pendingRedirect && pendingRedirect !== "/subscribe") {
        const wantsAdminRoute = pendingRedirect.includes("/admin");
        if (wantsAdminRoute && userType !== "admin") {
          navigate(isOrganizationUserType(userType) ? "/organization" : "/sessions");
        } else {
          navigate(pendingRedirect);
        }
        clearRedirectPath();
        return;
      }

      clearRedirectPath();

      if (userType === "admin") {
        navigate("/admin/home");
        return;
      }

      if (isOrganizationUserType(userType)) {
        navigate("/organization");
        return;
      }

      navigate("/sessions");
    },
    [clearRedirectPath, navigate, redirectPath],
  );

  const endSession = async (deleteAccount: boolean) => {
    isLoggingOutRef.current = true;
    setLoadingAuth(true);
    localStorage.removeItem("hub");
    clearRedirectPath();
    setUser(null);
    setIsImpersonatingFromAdmin(false);
    let firebaseDeleteFailed = false;
    if (deleteAccount) {
      if (!auth.currentUser) {
        // Nothing to delete means the login wasn't removed by us; say so.
        firebaseDeleteFailed = true;
        console.error("Post-delete Firebase auth cleanup skipped: no current user");
      } else {
        try {
          await deleteFirebaseUser(auth.currentUser);
        } catch (error) {
          firebaseDeleteFailed = true;
          console.error("Post-delete Firebase auth cleanup failed", error);
        }
      }
    }
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Failed to sign out from Firebase", error);
    } finally {
      isLoggingOutRef.current = false;
      setLoadingAuth(false);
    }
    navigate("/login");
    return { firebaseDeleteFailed };
  };

  const logout = async () => {
    await endSession(false);
  };

  const signOutDeletedAccount = () => endSession(true);

  // The axios interceptor signs out through here when a 401 survives a forced
  // token refresh. A ref keeps the registration stable across renders.
  const logoutRef = useRef(logout);
  logoutRef.current = logout;
  useEffect(() => {
    setSessionExpiredHandler(() => logoutRef.current());
    return () => setSessionExpiredHandler(null);
  }, []);

  const hydrateAppUser = useCallback(
    async (
      userData: AuthUserProfile,
      options?: { impersonatingFromAdmin?: boolean },
    ) => {
      if (!userData) return;
      let resolvedUser = userData;
      if (userData.subscriptionType === SubscriptionType.BETA) {
        try {
          resolvedUser = await claimBetaRewardService(
            normalizeUserIdentifier(userData).id,
          );
        } catch (error) {
          console.error("Failed to claim beta reward", error);
        }
      }

      const identifiedUser = normalizeUserIdentifier(resolvedUser);
      const derivedUserType = deriveUserTypeFromProfile(identifiedUser);
      const normalizedUser = {
        ...identifiedUser,
        userType: derivedUserType,
      };
      const impersonatingFromAdmin = options?.impersonatingFromAdmin || false;

      setUser(normalizedUser);
      setIsImpersonatingFromAdmin(impersonatingFromAdmin);
      persistHubSession(
        normalizedUser.id,
        normalizedUser.userType,
        impersonatingFromAdmin,
      );

      return normalizedUser;
    },
    [persistHubSession, setUser],
  );

  const navigateAfterHydration = useCallback(
    (normalizedUser?: any) => {
      if (!normalizedUser) return;
      if (normalizedUser.isVerified === false) {
        navigate("/verify-account");
        return;
      }
      handlePostLoginNavigation(normalizedUser.userType);
    },
    [handlePostLoginNavigation, navigate],
  );

  // Fetch the signed-in user from the non-admin /user endpoint, creating
  // their backend record on the fly if this is their first sign-in
  // (e.g. fresh email/password or OAuth sign-up). The backend returns 200
  // with a null/empty body when the doc doesn't exist, so we treat both
  // 404 and a missing userId as "needs to be created".
  const fetchOrCreateSelf = useCallback(
    async (firebaseUser: FirebaseUser): Promise<AuthUserProfile | null> => {
      const existingPromise = fetchOrCreateSelfPromisesRef.current[firebaseUser.uid];
      if (existingPromise) {
        return existingPromise;
      }

      const promise = (async () => {
        try {
          const existing = await fetchSelfUser(firebaseUser.uid);
          if (existing?.userId) {
            return existing;
          }
        } catch (error: any) {
          const status = error?.response?.status;
          if (status !== 404) {
            throw error;
          }
        }
        const providerIds = firebaseUser.providerData.map(
          (provider) => provider.providerId,
        );
        const requiresEmailVerification = providerIds.includes("password");
        const createdUser = await createUser({
          type: "user",
          email: firebaseUser.email ?? undefined,
          requireEmailVerification: requiresEmailVerification,
        });
        await analytics.track(POSTHOG_EVENTS.ACCOUNT_CREATED, {
          userId: createdUser?.userId || firebaseUser.uid,
          login_method: getLoginMethodFromProviderIds(providerIds),
          providerIds,
          hasEmail: Boolean(firebaseUser.email),
          requiresEmailVerification,
          source: "Hub/AuthProvider",
        });
        return createdUser;
      })();

      fetchOrCreateSelfPromisesRef.current[firebaseUser.uid] = promise;
      try {
        return await promise;
      } finally {
        delete fetchOrCreateSelfPromisesRef.current[firebaseUser.uid];
      }
    },
    [],
  );

  const refreshUser = useCallback(async () => {
    // Never raise loadingAuth here: it unmounts the tree, so on-mount callers remount and loop.
    const storedHub = localStorage.getItem("hub");
    try {
      if (storedHub) {
        const hub = JSON.parse(storedHub);
        if (hub.uuid) {
          try {
            const userData = await fetchSelfUser(hub.uuid);
            if (!userData) {
              throw new Error("Stored user response is empty");
            }
            await hydrateAppUser(userData, {
              impersonatingFromAdmin: !!hub.isImpersonatingFromAdmin,
            });
          } catch (error) {
            console.error("Failed to refresh user:", error);
          }
        }
      }
    } finally {
      setLoadingAuth(false);
    }
  }, [hydrateAppUser]);

  const getAppUserData = useCallback(
    async (userId: string, options?: { impersonatingFromAdmin?: boolean }) => {
      return fetchUser(userId).then(async (userData) => {
        if (!userData) throw new Error("Unable to load the requested account");
        const normalizedUser = await hydrateAppUser(userData, options);
        navigateAfterHydration(normalizedUser);
      });
    },
    [hydrateAppUser, navigateAfterHydration],
  );

  const loginWithUID = (uid: string) => {
    setLoadingAuth(true);
    getAppUserData(uid, { impersonatingFromAdmin: false })
      .catch((error) => {
        console.error("Failed to log in with UID:", error);
        toast.dismiss();
        toast.error(getAuthI18nValue("auth.failedLogin", "Failed to log in"));
      })
      .finally(() => setLoadingAuth(false));
  };

  const loginAsUserFromAdmin = (uid: string) => {
    clearRedirectPath();
    setLoadingAuth(true);
    getAppUserData(uid, { impersonatingFromAdmin: true })
      .catch((error) => {
        console.error("Failed to log in as user:", error);
        toast.dismiss();
        toast.error(
          getAuthI18nValue(
            "auth.failedLoginAsUser",
            "Failed to log in as user",
          ),
        );
      })
      .finally(() => setLoadingAuth(false));
  };

  const stopImpersonation = () => {
    setLoadingAuth(true);
    clearRedirectPath();
    // Hydration clears the impersonation flag and replaces the stored
    // session only after the original account is restored successfully.
    // Keep the exit control available if that request fails.

    const actualUid = auth.currentUser?.uid;
    if (!actualUid) {
      void logout();
      return;
    }

    getAppUserData(actualUid, { impersonatingFromAdmin: false })
      .catch((error) => {
        console.error("Failed to restore actual account:", error);
        toast.dismiss();
        toast.error(
          getAuthI18nValue("auth.failedRestoreAccount", "Failed to restore account"),
        );
      })
      .finally(() => setLoadingAuth(false));
  };

  // The auth listener subscribes once and reads these through a ref.
  // `navigate` changes identity on every route change (AuthProvider sits
  // outside <Routes>), and re-subscribing makes Firebase fire again, which
  // refetches the user and replaces the `user` object on every navigation.
  const authListenerDepsRef = useRef({
    clearRedirectPath,
    fetchOrCreateSelf,
    hydrateAppUser,
    navigate,
    navigateAfterHydration,
    redirectPath,
    rememberRedirectPath,
  });
  authListenerDepsRef.current = {
    clearRedirectPath,
    fetchOrCreateSelf,
    hydrateAppUser,
    navigate,
    navigateAfterHydration,
    redirectPath,
    rememberRedirectPath,
  };

  useEffect(() => {
    let isMounted = true;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const {
        clearRedirectPath,
        fetchOrCreateSelf,
        hydrateAppUser,
        navigate,
        navigateAfterHydration,
        redirectPath,
        rememberRedirectPath,
      } = authListenerDepsRef.current;
      try {
        if (isLoggingOutRef.current) {
          localStorage.removeItem("hub");
          clearRedirectPath();
          setUser(null);
          setIsImpersonatingFromAdmin(false);
          navigate("/login");
          return;
        }

        const storedHub = localStorage.getItem("hub");
        let parsedHub: any = null;
        if (storedHub) {
          try {
            parsedHub = JSON.parse(storedHub);
          } catch (error) {
            console.error("Failed to parse hub session", error);
          }
        }

        // Set when a fresh stored hub fails to restore with no Firebase user
        // behind it: Firebase signed out on its own (session revoked, account
        // disabled, password changed elsewhere) and the restore 401ed.
        let storedHubRestoreFailed = false;

        // If admin initiated an impersonation session, always prioritize that
        // context over Firebase auth user hydration to avoid identity flashing.
        if (parsedHub?.isImpersonatingFromAdmin && parsedHub?.uuid) {
          const timestamp = DateTime.fromISO(parsedHub.timestamp);
          const now = DateTime.now();
          const diff = now.diff(timestamp, "hours").hours;
          if (diff < 24) {
            const userData = await fetchUser(parsedHub.uuid);
            const normalizedUser = await hydrateAppUser(userData, {
              impersonatingFromAdmin: true,
            });
            if (normalizedUser || firebaseUser?.uid) {
              const pendingRedirect =
                redirectPath || localStorage.getItem(ADMIN_REDIRECT_STORAGE_KEY);
              const isLoginRoute = window.location.pathname === "/login";
              if (
                normalizedUser?.isVerified === false ||
                ((pendingRedirect || isLoginRoute) && normalizedUser)
              ) {
                navigateAfterHydration(normalizedUser);
              }
              return;
            }
            storedHubRestoreFailed = true;
          }
          localStorage.removeItem("hub");
          setIsImpersonatingFromAdmin(false);
        }

        if (firebaseUser?.uid) {
          const userData = await fetchOrCreateSelf(firebaseUser);
          if (!userData) {
            return;
          }
          const normalizedUser = await hydrateAppUser(userData, {
            impersonatingFromAdmin: false,
          });
          const pendingRedirect =
            redirectPath || localStorage.getItem(ADMIN_REDIRECT_STORAGE_KEY);
          const isLoginRoute = window.location.pathname === "/login";
          if (
            normalizedUser?.isVerified === false ||
            ((pendingRedirect || isLoginRoute) && normalizedUser)
          ) {
            navigateAfterHydration(normalizedUser);
          }
          return;
        }

        // A fresh hub restores without a Firebase login (UID login, E2E
        // personas). If it doesn't hydrate, fall through and end the session.
        if (storedHub && !storedHubRestoreFailed) {
          const hub = parsedHub || JSON.parse(storedHub);
          const timestamp = DateTime.fromISO(hub.timestamp);
          const now = DateTime.now();
          const diff = now.diff(timestamp, "hours").hours;
          if (diff < 24 && !!hub.uuid) {
            const userData = await fetchUser(hub.uuid);
            const normalizedUser = await hydrateAppUser(userData, {
              impersonatingFromAdmin: !!hub.isImpersonatingFromAdmin,
            });
            if (normalizedUser) {
              const pendingRedirect =
                redirectPath || localStorage.getItem(ADMIN_REDIRECT_STORAGE_KEY);
              const isLoginRoute = window.location.pathname === "/login";
              if (
                normalizedUser.isVerified === false ||
                pendingRedirect ||
                isLoginRoute
              ) {
                navigateAfterHydration(normalizedUser);
              }
              return;
            }
          }
        }

        localStorage.removeItem("hub");
        setUser(null);
        setIsImpersonatingFromAdmin(false);
        const isProtectedRoute = ![
          "/login",
          "/organization/setup",
          "/shared",
          "/verify",
          "/join",
        ].some((path) => window.location.pathname.includes(path));
        if (isProtectedRoute) {
          rememberRedirectPath();
          navigate("/login");
        }
      } catch (error) {
        console.error("Auth state hydration error:", error);
        localStorage.removeItem("hub");
      } finally {
        if (isMounted) {
          setLoadingAuth(false);
        }
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  const register = (email: string, password: string) => {
    setLoadingAuth(true);
    createUserWithEmailAndPassword(auth, email, password)
      .then(() => {
        navigate("/verify-account");
      })
      .catch((error) => {
        console.error("Failed to register:", error);
        toast.dismiss();
        setLoadingAuth(false);
        const code = error?.code;
        if (code === "auth/email-already-in-use") {
          toast.error("An account already exists with this email.");
        } else if (code === "auth/invalid-email") {
          toast.error("Please enter a valid email address.");
        } else if (code === "auth/weak-password") {
          toast.error("Password must be at least 6 characters.");
        } else {
          toast.error("Failed to create account");
        }
      });
  };

  const login = (user: string, password: string) => {
    setSigningIn(true);
    signInWithEmailAndPassword(auth, user, password)
      .then(async (credential) => {
        const userData = await fetchOrCreateSelf(credential.user);
        if (!userData) {
          throw new Error("Authenticated user response is empty");
        }
        const normalizedUser = await hydrateAppUser(userData, {
          impersonatingFromAdmin: false,
        });
        await analytics.track(POSTHOG_EVENTS.LOGIN, {
          userId: normalizedUser?.id || normalizedUser?.userId,
          login_method: "email_password",
          providerIds: credential.user.providerData.map(
            (provider) => provider.providerId,
          ),
          hasEmail: Boolean(credential.user.email),
          source: "Hub/AuthProvider",
        });
        navigateAfterHydration(normalizedUser);
      })
      .catch((error) => {
        console.log(error);
        toast.dismiss();
        toast.error(getAuthI18nValue("auth.failedLogin", "Failed to log in"));
      })
      .finally(() => {
        setSigningIn(false);
      });
  };

  // Shared by every sign-in that hands back a Firebase credential.
  const signInWith = async (
    getCredential: () => Promise<UserCredential>,
    loginMethod: string,
  ) => {
    setSigningIn(true);
    try {
      const credential = await getCredential();
      const userData = await fetchOrCreateSelf(credential.user);
      if (!userData) {
        throw new Error("Authenticated user response is empty");
      }
      const normalizedUser = await hydrateAppUser(userData, {
        impersonatingFromAdmin: false,
      });
      await analytics.track(POSTHOG_EVENTS.LOGIN, {
        userId: normalizedUser?.id || normalizedUser?.userId,
        login_method: loginMethod,
        providerIds: credential.user.providerData.map(
          (providerData) => providerData.providerId,
        ),
        hasEmail: Boolean(credential.user.email),
        source: "Hub/AuthProvider",
      });
      navigateAfterHydration(normalizedUser);
    } catch (error) {
      console.log(error);
      toast.dismiss();
      toast.error(getAuthI18nValue("auth.failedLogin", "Failed to log in"));
      throw error;
    } finally {
      setSigningIn(false);
    }
  };

  const loginWithProvider = async (
    provider: FirebaseAuthProvider,
    loginMethod: string,
  ) => {
    try {
      await signInWith(() => signInWithPopup(auth, provider), loginMethod);
    } catch {
      // signInWith already told the user.
    }
  };

  // LINE has no Firebase provider: the server mints a custom token for it.
  const loginWithLineToken = (customToken: string) =>
    signInWith(() => signInWithCustomToken(auth, customToken), "line");

  const verifyAccountCode = async (code: string) => {
    try {
      const verifiedUser = await verifyUserCode(code);
      const normalizedUser = await hydrateAppUser(verifiedUser, {
        impersonatingFromAdmin: false,
      });
      toast.dismiss();
      toast.success(
        getAuthI18nValue("auth.accountVerified", "Account verified"),
      );
      navigateAfterHydration(normalizedUser);
    } catch (error: any) {
      console.error("Failed to verify account:", error);
      toast.dismiss();
      const detail = error?.response?.data?.detail;
      const codeValue = detail?.code;
      if (codeValue === "EXPIRED_CODE") {
        toast.error(
          getAuthI18nValue(
            "auth.expiredVerificationCode",
            "That code has expired. Please request a new one.",
          ),
        );
      } else {
        toast.error(
          getAuthI18nValue(
            "auth.invalidVerificationCode",
            "Invalid verification code",
          ),
        );
      }
      throw error;
    }
  };

  const resendVerificationCode = async () => {
    try {
      const result = await resendUserVerificationCode();
      toast.dismiss();
      if (result.verified) {
        toast.success(
          getAuthI18nValue(
            "auth.accountAlreadyVerified",
            "Account already verified",
          ),
        );
        await refreshUser();
      } else {
        // Name the address: a code that never arrives is usually a typo'd
        // signup email, and this is where the user can spot it.
        toast.success(
          user?.email
            ? `${getAuthI18nValue(
                "auth.verificationCodeSentTo",
                "Verification code sent to",
              )} ${user.email}`
            : getAuthI18nValue(
                "auth.verificationCodeSent",
                "Verification code sent",
              ),
        );
      }
    } catch (error) {
      console.error("Failed to resend verification code:", error);
      toast.dismiss();
      toast.error(
        getAuthI18nValue(
          "auth.failedSendVerificationCode",
          "Couldn't send verification code",
        ),
      );
      throw error;
    }
  };

  const { pathname } = useLocation();
  const PUBLIC_PREFIXES = [
    "/verify",
    "/verify-account",
    "/organization/setup",
    "/shared/",
    "/tournament/",
    "/join/",
  ];
  const isPublicRoute = PUBLIC_PREFIXES.some((prefix) =>
    pathname.startsWith(prefix),
  );

  return (
    <AuthContext.Provider
      value={{
        loadingAuth,
        signingIn,
        user,
        isImpersonatingFromAdmin,
        login,
        loginWithProvider,
        loginWithLineToken,
        register,
        logout,
        signOutDeletedAccount,
        setUser,
        loginWithUID,
        loginAsUserFromAdmin,
        stopImpersonation,
        refreshUser,
        resendVerificationCode,
        verifyAccountCode,
        rememberRedirectPath,
      }}
    >
      {loadingAuth && !isPublicRoute ? (
        <AuthLoadingState
          loadingLabel={getAuthI18nValue(
            "auth.loadingAccount",
            "Loading account...",
          )}
        />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};
